import { sql } from "./db.js";
import { applyDemoPlatformDefaults, applyDemoPlanAndFlags, DEMO_PLAN_BY_SLUG } from "./demo-db-helpers.js";
import { publishSeededTenantExperienceFromDb } from "./experience-definition/publish-seeded-experience.js";
import { seedDemoAnalyticsHistory } from "./demo-analytics-seed.js";
import { seedDemoMetricsFullData } from "./demo-metrics-full-seed.js";
import { seedDemoEnterpriseFeatures } from "./demo-enterprise-features-seed.js";
import { seedDemoOperationalStaff } from "./demo-operational-staff-seed.js";

/** Demos de marketplace con seed / reset unificados. */
export type DemoSlug =
  | "carniceria-structura"
  | "dietetica-structura"
  | "rotiseria-structura"
  | "farmacia-structura"
  | "moda-structura"
  | "bazar-structura"
  | "cafe-structura"
  | "pistacho"
  | "americanburger"
  | "libreria-structura";

export const DEMO_SLUGS: DemoSlug[] = [
  "carniceria-structura",
  "dietetica-structura",
  "rotiseria-structura",
  "farmacia-structura",
  "moda-structura",
  "bazar-structura",
  "cafe-structura",
  "pistacho",
  "americanburger",
  "libreria-structura",
];

export const DEMO_TENANT_IDS: Record<DemoSlug, string> = {
  "carniceria-structura": "t-carniceria-structura",
  "dietetica-structura": "t-dietetica-structura",
  "rotiseria-structura": "t-rotiseria-structura",
  "farmacia-structura": "t-farmacia-structura",
  "moda-structura": "t-moda-structura",
  "bazar-structura": "t-bazar-structura",
  "cafe-structura": "t-cafe-structura",
  pistacho: "t-pistacho",
  americanburger: "t-burger-bosch",
  "libreria-structura": "t-libreria-structura",
};

/** Targets extras (fuera de DEMO_SLUGS) para analytics + plan segregation CLI. */
export const EXTENDED_DEMO_ANALYTICS_TARGETS: Array<{
  slug: string;
  tenantId: string;
  skipOrderDays?: number;
}> = [
  { slug: "el-cortejo", tenantId: "t-el-cortejo", skipOrderDays: 30 },
  { slug: "el-bodegon-de-san-lorenzo", tenantId: "t-bodegon-san-lorenzo", skipOrderDays: 0 },
  { slug: "petfy", tenantId: "t-petfy", skipOrderDays: 0 },
  { slug: "tienda-bebidas", tenantId: "t-tienda-bebidas", skipOrderDays: 0 },
];

const NICHO_SLUGS: Record<string, string> = {
  "farmacia-structura": "farmacia-structura",
  "moda-structura": "moda-structura",
  "bazar-structura": "bazar-structura",
  "cafe-structura": "cafe-structura",
  pistacho: "pistacho",
  americanburger: "americanburger",
  "libreria-structura": "libreria-structura",
  /** @deprecated */
  burgerbosch: "americanburger",
};

export function resolveDemoSlug(input?: string | null): DemoSlug | null {
  if (!input) return null;
  const normalized = NICHO_SLUGS[input] ?? input;
  if ((DEMO_SLUGS as string[]).includes(normalized)) return normalized as DemoSlug;
  const entry = Object.entries(DEMO_TENANT_IDS).find(([, tenantId]) => tenantId === input);
  return entry ? (entry[0] as DemoSlug) : null;
}

export { DEMO_PLAN_BY_SLUG };

async function seedDemoExperience(slug: DemoSlug, tenantId: string) {
  const db = sql();
  const rows = await db`
    SELECT product_flags FROM sm_tenants WHERE id = ${tenantId} LIMIT 1
  `;
  const row = rows[0] as Record<string, unknown> | undefined;
  if (!row) return;
  const flags: Record<string, unknown> = {
    ...((row.product_flags as Record<string, unknown>) ?? {}),
    is_demo: true,
  };
  // Moda demo: merchant UI debe funcionar aunque el plan quede starter-like.
  if (slug === "moda-structura") {
    flags.integrations_merchant = true;
  }
  await db`UPDATE sm_tenants SET product_flags = ${JSON.stringify(flags)}::jsonb WHERE id = ${tenantId}`;
  await publishSeededTenantExperienceFromDb(db, tenantId, { demoSlug: slug });
}

export async function seedDemoMarket(
  slug: DemoSlug,
  opts?: { resetOrdersOnly?: boolean; skipImages?: boolean; refreshImagesOnly?: boolean },
): Promise<Record<string, unknown>> {
  let result: Record<string, unknown>;

  if (slug === "carniceria-structura") {
    const { seedCarniceriaDemo } = await import("./carniceria-demo-seed.js");
    result = await seedCarniceriaDemo(opts);
  } else if (slug === "dietetica-structura") {
    const { seedDieteticaDemo } = await import("./dietetica-demo-seed.js");
    result = await seedDieteticaDemo(opts);
  } else if (slug === "rotiseria-structura") {
    const { seedRotiseriaDemo } = await import("./rotiseria-demo-seed.js");
    result = await seedRotiseriaDemo(opts);
  } else if (NICHO_SLUGS[slug]) {
    const { seedNichoDemo } = await import("./nicho-demos-seed.js");
    const { NICHO_DEMOS } = await import("./data/nicho-demos-catalog.js");
    const demo = NICHO_DEMOS.find((d) => d.slug === slug);
    if (!demo) {
      const { seedRotiseriaDemo } = await import("./rotiseria-demo-seed.js");
      result = await seedRotiseriaDemo(opts);
    } else {
      result = await seedNichoDemo(demo);
    }
  } else {
    const { seedRotiseriaDemo } = await import("./rotiseria-demo-seed.js");
    result = await seedRotiseriaDemo(opts);
  }

  const skipPlatformDefaults = opts?.resetOrdersOnly === true || opts?.refreshImagesOnly === true;
  const tenantId = DEMO_TENANT_IDS[slug];

  if (!skipPlatformDefaults) {
    const platform = await applyDemoPlatformDefaults(sql(), tenantId, slug);
    result = { ...result, platformDefaults: platform };
  }

  try {
    await seedDemoExperience(slug, tenantId);
    result = { ...result, experienceSeeded: true };
  } catch (e) {
    result = {
      ...result,
      experienceSeeded: false,
      experienceError: e instanceof Error ? e.message : String(e),
    };
  }

  if (!skipPlatformDefaults) {
    try {
      const plan = await applyDemoPlanAndFlags(sql(), tenantId, slug);
      result = { ...result, demoPlan: plan.planId };
    } catch (e) {
      result = {
        ...result,
        demoPlanError: e instanceof Error ? e.message : String(e),
      };
    }

    try {
      const analytics = await seedDemoAnalyticsHistory(sql(), tenantId, slug, { days: 60 });
      result = { ...result, analyticsSeed: analytics };
    } catch (e) {
      result = {
        ...result,
        analyticsSeedError: e instanceof Error ? e.message : String(e),
      };
    }

    try {
      const metricsFull = await seedDemoMetricsFullData(sql(), tenantId, slug);
      result = { ...result, metricsFull };
    } catch (e) {
      result = {
        ...result,
        metricsFullError: e instanceof Error ? e.message : String(e),
      };
    }

    let businessProfile: string | null = null;
    try {
      const profileRows = await sql()`
        SELECT business_profile FROM sm_tenant_config WHERE tenant_id = ${tenantId} LIMIT 1
      `;
      businessProfile = (profileRows[0]?.business_profile as string | null) ?? null;
    } catch {
      businessProfile = null;
    }

    try {
      const enterpriseFeatures = await seedDemoEnterpriseFeatures(sql(), tenantId, slug, businessProfile);
      result = { ...result, enterpriseFeatures };
    } catch (e) {
      result = {
        ...result,
        enterpriseFeaturesError: e instanceof Error ? e.message : String(e),
      };
    }

    try {
      const operationalStaff = await seedDemoOperationalStaff(sql(), tenantId, slug, businessProfile);
      result = { ...result, operationalStaff };
    } catch (e) {
      result = {
        ...result,
        operationalStaffError: e instanceof Error ? e.message : String(e),
      };
    }
  }

  return result;
}
